<template>
  <div style="height: 100%">
    <!-- HEADER -->
    <v-row class="rowView">
      <v-col
        class="colView h5"
        style="color: var(--color-text)"
      >
        Overconsumption
      </v-col>

      <v-spacer />

      <v-btn
        text
        small
        class="ma-auto pa-1"
        color="var(--color-text)"
        @click="dialog = true"
      >
        Overconsumption History
      </v-btn>
    </v-row>

    <!-- MAIN CHART -->
    <v-row
      class="rowView"
      style="height: calc(100% - 25px)"
    >
      <v-col
        class="colView"
        :cols="12"
      >
        <v-card style="background-color: var(--color-bg) !important">
          <v-card-title>
            <div
              class="bodyBold"
              style="color: var(--color-text)"
            >
              {{ consTitles[consModel] }}
            </div>

            <v-spacer />

            <v-btn-toggle
              v-model="consModelProxy"
              mandatory
              class="my-auto mx-1"
            >
              <v-btn x-small>
                Cumulative
              </v-btn>
              <v-btn x-small>
                Daily
              </v-btn>
            </v-btn-toggle>
          </v-card-title>

          <v-card-actions>
            <highcharts
              ref="mainChart"
              :key="mainChartKey"
              :options="clonedFoulingConsData"
              style="width: 100%; height: 100%"
            />
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- HISTORY DIALOG -->
    <!-- transition="false" removes scale animation that breaks Highcharts getBoundingClientRect -->
    <!-- eager renders the chart immediately so dimensions are correct on open -->
    <v-dialog
      v-model="dialog"
      max-width="1400px"
      transition="false"
      :eager="true"
    >
      <v-card style="background-color: var(--color-bg) !important; height: 100%">
        <v-card-title>
          <div
            class="bodyBold"
            style="color: var(--color-text)"
          >
            Overconsumption History
          </div>

          <v-spacer />

          <v-btn-toggle
            v-model="consModelHistoryProxy"
            mandatory
            class="my-auto mx-1"
          >
            <v-btn x-small>
              Cumulative
            </v-btn>
            <v-btn x-small>
              Daily
            </v-btn>
          </v-btn-toggle>
        </v-card-title>

        <v-card-actions style="padding: 0 !important">
          <highcharts
            ref="historyChart"
            :key="historyChartKey"
            :options="clonedAllOverconsumption"
            style="width: 100%; height: 500px; background-color: var(--color-bg) !important"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const tooltipFormatter = {
  shared: false,
  useHTML: true,
  formatter() {
    const date = new Date(this.x).toLocaleDateString("en-GB", {
      year: "numeric", month: "short", day: "2-digit",
    });
    const value = typeof this.y === "number"
      ? this.y.toFixed(2)
      : Number(this.y).toFixed(2);
    return `<b>Date:</b> ${date}<br/><b>${this.series.name}:</b> ${value}`;
  },
};

export default {
  name: "OverconsumptionChartPanel",

  model: {
    prop: "value",
    event: "input",
  },

  props: {
    value:              { type: Boolean, default: false },
    consTitles:         { type: Array,   required: true },
    consModel:          { type: Number,  required: true },
    consModelHistory:   { type: Number,  required: true },
    foulingConsData:    { type: Object,  required: true },
    allOverconsumption: { type: Object,  required: true },
    eventDict:          { type: Array,   required: true },
  },

  data() {
    return {
      mainChartKey: 0,
      historyChartKey: 0,
    };
  },

  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },

    consModelProxy: {
      get() { return this.consModel; },
      set(v) { this.$emit("cons-model-change", v); },
    },

    consModelHistoryProxy: {
      get() { return this.consModelHistory; },
      set(v) { this.$emit("cons-model-history-change", v); },
    },

    clonedFoulingConsData() {
      if (!this.foulingConsData) return {};
      const clone = JSON.parse(JSON.stringify(this.foulingConsData));
      clone.tooltip = tooltipFormatter;
      return clone;
    },

    clonedAllOverconsumption() {
      if (!this.allOverconsumption) return {};
      const clone = JSON.parse(JSON.stringify(this.allOverconsumption));
      clone.tooltip = tooltipFormatter;
      return clone;
    },
  },

  watch: {
    // Parent changes main chart data → force chart rebuild
    foulingConsData: {
      deep: true,
      handler() {
        this.mainChartKey++;
      },
    },

    // Parent changes history chart data → force chart rebuild
    allOverconsumption: {
      deep: true,
      handler() {
        this.historyChartKey++;
      },
    },

    // Dialog opens → reflow after eager render settles
    dialog(val) {
      if (!val) return;
      const self = this;
      setTimeout(function () {
        if (self.$refs.historyChart && self.$refs.historyChart.chart) {
          self.$refs.historyChart.chart.reflow();
          self.$refs.historyChart.chart.pointer.chartPosition = null;
        }
      }, 50);
    },
  },
};
</script>

<style scoped>
.v-btn {
  background-color: var(--color-primary) !important;
  color: var(--color-text) !important;
  border-color: var(--color-border) !important;
}
.v-btn:hover {
  background-color: var(--color-primary-light) !important;
}
.v-btn--disabled {
  background-color: var(--color-neutral) !important;
  color: var(--color-text-muted) !important;
}
.v-btn--plain {
  background-color: transparent !important;
  color: var(--color-text) !important;
}
.v-btn--plain:hover {
  background-color: var(--color-overlay) !important;
}
</style>
